import React, { Component } from 'react';
import { shape, string, bool, arrayOf, func, oneOf } from 'prop-types';
import { gql, graphql } from 'react-apollo';
import { connect } from 'react-redux';
import invariant from 'invariant';
import { locationShape as routerLocationShape } from 'react-router/lib/PropTypes';
import { operations } from 'state/ducks/map';
import SegmentGroup from 'components/Map/MapComponents/SegmentGroup';
import RouteGroup from 'components/Map/MapComponents/RouteGroup';
import LocationMarkerGroup from 'components/Map/MapComponents/LocationMarkerGroup';
import locationShape from 'propTypes/locationShape';
import LoadingIndicator from 'components/LoadingIndicator';
import coord from 'propTypes/coord';

export const LiveMapQuery = gql`
  query LiveMap {
    locations {
      id
      lat
      lng
      name
      trackingDevice {
        id
        name
        status
        batteryVoltage
        temperature
        reportInterval
        lastUpdate
        numberOfHitsToday
      }
    }
    segments {
      id
      status
      path
      bounds
      liveTravelTime
      expectedTravelTime
      oppositeSegmentId
      liveSpeed
      from {
        id
        name
      }
      to {
        id
        name
      }
    }
    routes {
      id
      status
      normalSpeed
      liveSpeed
      distance
      liveTravelTime
      oppositeRouteId
      from {
        id
        name
      }
      to {
        id
        name
      }
      segments {
        id
        status
        path
        bounds
        liveTravelTime
        expectedTravelTime
        liveSpeed
        from {
          id
          name
        }
        to {
          id
          name
        }
      }
    }
  }
`;

class LiveMap extends Component {
  componentWillUpdate(nextProps) {
    const {
      location: { pathname },
      data: { segments, locations, routes },
      setBounds,
      setAllFeatureBounds,
      setCenter,
    } = nextProps;

    /* only update bounds on first data render */
    if (this.props.data.loading && !nextProps.loading) {
      const bounds = locations.reduce((acc, v) => [...acc, [v.lat, v.lng]], []);
      setAllFeatureBounds(bounds);
      return;
    }

    const { params: { id: oldId } } = this.props;
    const { params: { id: newId } } = nextProps;

    if (newId && newId !== oldId) {
      if (pathname.includes('locations')) {
        const selectedLocation = locations.find(v => v.id === newId);
        invariant(selectedLocation, `Failed to find id ${newId} in locations collection`);
        setCenter([selectedLocation.lat, selectedLocation.lng]);
      } else if (pathname.includes('segments')) {
        const selectedSegment = segments.find(v => v.id === newId);
        invariant(selectedSegment, `Failed to find id ${newId} in segments collection`);
        setBounds(selectedSegment.bounds);
      } else if (pathname.includes('routes')) {
        const selectedRoute = routes.find(v => v.id === newId);
        invariant(selectedRoute, `Failed to find id ${newId} in routes collection`);

        // @TODO move this to GQL
        const routeBounds = selectedRoute.segments.reduce((acc, v) => [...acc, ...v.bounds], []);

        setBounds(routeBounds);
      }
    }
  }

  render() {
    const {
      location: { pathname },
      params,
      data: { loading, segments, locations, routes },
      viewMode,
    } = this.props;

    if (loading) {
      return <LoadingIndicator />;
    }

    /*
      Decide if we need to show routes. The panel routes overrule the viewMode setting. This
      is a bit messy and could probably be improved upon */
    const showRoutes =
      pathname.includes('routes') || (!pathname.includes('segments') && viewMode === 'routes');

    return (
      <div>
        {/* @TODO this is something which could be improved. The locations need the segments
        in order to figure out if they should be highlighted when a segment id is in the currentHover
        state. We could maybe figure that out at this level and pass the ids to highlight as props
        to each of the group components. We are already connected to the store at this level and
        we have all the data to figure out what ids need highlighting */}
        <LocationMarkerGroup locations={locations} segments={segments} routeParams={params} />

        {showRoutes
          ? <RouteGroup routes={routes} routeParams={params} />
          : <SegmentGroup segments={segments} routeParams={params} />}
      </div>
    );
  }
}

LiveMap.propTypes = {
  data: shape({
    loading: bool.isRequired,
    segments: arrayOf(
      shape({
        id: string,
        path: arrayOf(coord),
        from: locationShape,
        to: locationShape,
      })
    ),
    locations: arrayOf(locationShape),
  }).isRequired,
  location: routerLocationShape,
  params: shape({
    id: string,
  }).isRequired,
  setBounds: func.isRequired,
  setAllFeatureBounds: func.isRequired,
  setCenter: func.isRequired,
  viewMode: oneOf(['segments', 'routes']).isRequired,
};

const mapStateToProps = ({ map }) => ({
  viewMode: map.viewMode,
});

const LiveMapWithData = graphql(LiveMapQuery)(LiveMap);
const ConnectedLiveMapWithData = connect(mapStateToProps, operations)(LiveMapWithData);

export default ConnectedLiveMapWithData;



// WEBPACK FOOTER //
// ./src/components/Map/Live/index.js